import React, { useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { styled, keyframes } from '@mui/material/styles';
import { Button, Typography, Container, Box, CircularProgress, Card } from '@mui/material';
// components
import Page from '../components/Page';
import Iconify from '../components/Iconify';

// ----------------------------------------------------------------------

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 72, 66, 0.4);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 16px rgba(255, 72, 66, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 72, 66, 0);
  }
`;

const pulseBlue = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(29, 141, 255, 0.4);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 16px rgba(29, 141, 255, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(29, 141, 255, 0);
  }
`;

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  alignItems: 'center',
  justifyContent: 'center',
  background: `radial-gradient(circle at 50% 50%, ${theme.palette.background.neutral} 0%, ${theme.palette.background.default} 100%)`,
  padding: theme.spacing(3),
}));

const CardStyle = styled(Card)(({ theme }) => ({
  maxWidth: 480,
  width: '100%',
  padding: theme.spacing(6, 4),
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.customShadows?.z24 || theme.shadows[24],
  backgroundColor: theme.palette.background.paper,
}));

const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isNotFound',
})(({ theme, isNotFound }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  backgroundColor: isNotFound ? theme.palette.error.lighter : theme.palette.info.lighter,
  color: isNotFound ? theme.palette.error.main : theme.palette.info.main,
  animation: `${isNotFound ? pulse : pulseBlue} 2.5s infinite ease-in-out`,
}));

// ----------------------------------------------------------------------

BootstrapError.propTypes = {
  error: PropTypes.shape({
    type: PropTypes.string.isRequired,
    message: PropTypes.string,
  }).isRequired,
  onRetry: PropTypes.func.isRequired,
};

export default function BootstrapError({ error, onRetry }) {
  const [isRetrying, setIsRetrying] = useState(false);

  const isNotFound = error.type === 'NOT_FOUND';
  const title = isNotFound ? 'Application Not Configured' : 'Gateway Connection Offline';
  
  const handleRetry = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      await onRetry();
    } catch (err) {
      console.error('Retry failed:', err);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <Page title={title}>
      <RootStyle>
        <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
          <CardStyle>
            <IconWrapper isNotFound={isNotFound}>
              <Iconify
                icon={isNotFound ? 'eva:shield-off-fill' : 'eva:wifi-off-fill'}
                sx={{ width: 40, height: 40 }}
              />
            </IconWrapper>

            <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
              {title}
            </Typography>

            <Typography sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
              {isNotFound
                ? 'This domain is not currently mapped or configured with any active white-label application on our servers. Please verify the web address or contact support.'
                : "We are unable to establish a secure connection to the gateway control plane. Please check your internet connection and click retry to attempt reconnecting."}
            </Typography>

            {!isNotFound && (
              <Button
                size="large"
                variant="contained"
                onClick={handleRetry}
                disabled={isRetrying}
                startIcon={
                  isRetrying ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Iconify icon="eva:refresh-fill" />
                  )
                }
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 1,
                  boxShadow: (theme) => theme.customShadows?.primary,
                }}
              >
                {isRetrying ? 'Reconnecting...' : 'Retry Connection'}
              </Button>
            )}

            <Box
              sx={{
                mt: 5,
                pt: 3,
                width: '100%',
                borderTop: (theme) => `1px dashed ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                color: 'text.disabled',
              }}
            >
              <Iconify icon="eva:lock-fill" sx={{ width: 14, height: 14 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                SECURE ENDPOINT VERIFICATION
              </Typography>
            </Box>
          </CardStyle>
        </Container>
      </RootStyle>
    </Page>
  );
}
